import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Box, IconButton, CircularProgress } from '@mui/material';
import { createMatchEvent, getCurrentLocation, getOpenAssignments } from '../../services/api/eventService';
import { useCameraCaptures } from '../../contexts/cameraCapturesContext';
import { useCameraStream } from '../../hooks/useCameraStream';
import { useFaceMatching } from '../../hooks/useFaceMatching';
import { useSettings } from '../../contexts/settingsContext';
import { UI_TIMINGS } from '../../config/appConfig';
import CancelIcon from '@mui/icons-material/Cancel';
import '../css/StealthCamera.css';

// Stealth Camera - Fullscreen camera with tap-to-capture and silent background processing
// - Fills entire screen with camera viewport
// - Tap anywhere to capture and automatically send for face recognition
// - If match found, saves to history silently with no visual confirmation
// - Red circle exit button in bottom left to close and return to app

export default function StealthCamera({ open, onClose }) {
  const canvasRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [flash, setFlash] = useState(false);
  const [captureCount, setCaptureCount] = useState(0);
  const [matchCount, setMatchCount] = useState(0);

  const { addCapture } = useCameraCaptures();
  const settings = useSettings();
  const { autoCreateEventOnAIMatch } = settings;

  // Use camera stream hook
  const { videoRef, isRunning, error, startCamera, stopCamera, captureFrame } = useCameraStream({
    autoStart: false,
    onError: (err) => {
      console.error('[Stealth] Camera error:', err);
    }
  });

  // Stable callback for match success - uses useCallback to maintain reference
  const handleMatchSuccess = useCallback(async (result, matchedPersons) => {
    setMatchCount(prev => {
      return prev + 1;
    });

    // Only create events manually if auto-create is disabled
    // If auto-create is enabled, useFaceMatching will handle it
    if (!autoCreateEventOnAIMatch && matchedPersons.length > 0) {
      const topMatchedPerson = matchedPersons[0];
      const topMatch = result.matches[0];
      const personName = `${topMatchedPerson.first_name || ''} ${topMatchedPerson.last_name || ''}`.trim() || 'Unknown';

      // Get location and open assignment for context
      const [location, openAssignments] = await Promise.all([
        getCurrentLocation(settings),
        getOpenAssignments().catch(() => [])
      ]);

      const openAssignment = openAssignments.length > 0 ? openAssignments[0] : null;

      // Save to history silently using createMatchEvent
      try {
        await createMatchEvent({
          personName,
          personImage: topMatchedPerson.image || '',
          person: topMatchedPerson,
          openAssignment,
          location,
          matchSource: 'face_match', // AI face match from stealth mode
          matchAccuracy: topMatch.similarity / 100, // Convert to 0-1 range
          note: 'Image Captured in Stealth Mode' // Note for stealth matches
        });
      } catch (historyError) {
        console.error('[Stealth] Failed to save to history:', historyError);
      }
    } else if (autoCreateEventOnAIMatch) {
    }
  }, [autoCreateEventOnAIMatch]);

  // Stable callback for no match
  const handleNoMatch = useCallback(() => {
  }, []);

  // Stable callback for errors
  const handleError = useCallback((err) => {
    console.error('[Stealth] Processing failed:', err);
  }, []);

  // Use face matching hook in silent mode with stable callbacks
  const { matchFace } = useFaceMatching({
    silent: true,
    onSuccess: handleMatchSuccess,
    onNoMatch: handleNoMatch,
    onError: handleError
  });

  // Start camera when component opens and reset counters
  useEffect(() => {
    if (open) {
      startCamera({ video: { facingMode: 'environment' }, audio: false });
      setCaptureCount(0);
      setMatchCount(0);
    } else {
      stopCamera();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]); // Only run when 'open' changes, not when startCamera/stopCamera change

  async function handleTapCapture() {
    if (!isRunning || isProcessing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    // Flash animation
    setFlash(true);
    setTimeout(() => setFlash(false), UI_TIMINGS.CAMERA_FLASH_DURATION);

    // Capture image using hook
    const dataUrl = captureFrame(canvas);
    if (!dataUrl) return;

    // Process in background
    processImageSilently(dataUrl);
  }

  async function processImageSilently(dataUrl) {
    setIsProcessing(true);

    // Add capture to shared context immediately
    const captureId = addCapture(dataUrl, 'stealth');
    setCaptureCount(prev => {
      return prev + 1;
    });

    try {
      // Use face matching hook (handles compression, network detection, and matching)
      await matchFace(captureId, dataUrl, null);
    } catch (err) {
      // Error already logged by hook
    } finally {
      setIsProcessing(false);
    }
  }

  function handleClose() {
    stopCamera();
    onClose();
  }

  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        bgcolor: 'black',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}
    >
      {/* Video viewport - fullscreen */}
      <div
        className="stealth-camera-viewport"
        onClick={handleTapCapture}
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          cursor: isRunning ? 'pointer' : 'default'
        }}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />

        {/* Flash effect */}
        {flash && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'white',
              animation: 'flash 120ms ease-out',
              pointerEvents: 'none'
            }}
          />
        )}

        {/* Processing indicator - subtle */}
        {isProcessing && (
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              opacity: 0.6
            }}
          >
            <CircularProgress size={24} sx={{ color: 'white' }} />
          </Box>
        )}

        {/* Capture/Match counter - subtle */}
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            color: 'white',
            fontSize: '14px',
            fontWeight: 500,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            padding: '6px 12px',
            borderRadius: '4px',
            opacity: 0.7,
            fontFamily: 'monospace',
            letterSpacing: '0.5px'
          }}
        >
          {matchCount} / {captureCount}
        </Box>

        {/* Error message */}
        {error && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: 'white',
              textAlign: 'center',
              padding: 2,
              bgcolor: 'rgba(0,0,0,0.7)',
              borderRadius: 1
            }}
          >
            Error: {error}
          </Box>
        )}
      </div>

      {/* Exit button - red circle in bottom left */}
      <IconButton
        onClick={handleClose}
        sx={{
          position: 'absolute',
          bottom: 24,
          left: 24,
          width: 64,
          height: 64,
          bgcolor: 'red',
          color: 'white',
          '&:hover': {
            bgcolor: '#b71c1c'
          },
          boxShadow: 3
        }}
      >
        <CancelIcon sx={{ fontSize: 40 }} />
      </IconButton>

      {/* Hidden canvas for image capture */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </Box>
  );
}
